import { ArrowRightIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useAuth } from "@/features/auth/auth-context"
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import type { DashboardSummaryResponse } from "@/lib/endpoints"
import { routes } from "@/routes/paths"

/**
 * "Delivered, not yet billed: 3" — §17.7's whole payoff, on the first screen he
 * sees (§11.2).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS NUMBER IS THE FEATURE. Delivery challans exist so that goods can move
 * before the bill is written. The failure that costs a small fabricator real
 * money is not the paperwork — it is the load that went out in March and was
 * never billed, discovered in October by an accountant reconciling a challan
 * book. The register can already answer "what have I delivered and not billed?",
 * but only if someone thinks to ask it, and nobody opens a register to ask a
 * question they have forgotten they have. Until the count is on the home screen,
 * the feature is a filing cabinet.
 *
 * IT IS ABSENT AT ZERO, DELIBERATELY, AND THAT IS NOT THE SAME DECISION AS THE
 * LAST-BILL TILE'S. §11.2 hides an absent document rather than showing 0,
 * because a zero on a new install reads as a broken counter. Here the reason is
 * different and stronger: this card is a REMINDER, and a reminder that is always
 * on screen saying "nothing to do" is the thing the eye learns to skip. When it
 * appears, it is because something is genuinely waiting — which is what makes it
 * worth looking at the day it does.
 *
 * THE KEY IS OPTIONAL ON THE WIRE, AND ITS ABSENCE MEANS "NOT AVAILABLE".
 * `GET /dashboard/summary` is not behind a feature flag while every challan
 * route is behind `requireFlag("challans")`, so the API omits the key entirely
 * when the module is off rather than sending 0 — a zero would still announce
 * that unreleased work exists (§18.3). Undefined and zero therefore render the
 * same nothing, and neither needs a branch of its own.
 *
 * A COUNT, NEVER A RUPEE FIGURE. §11.2 forbids the home screen implying payment
 * status, and §17.7 is explicit that a challan's value "is not money anyone
 * owes". A total of unbilled deliveries sitting beside "Billed this month" would
 * be read as a debtors figure — the one claim this app cannot make until §17.5
 * exists — so no amount appears here at all.
 *
 * CAPABILITY, NEVER ROLE (§2.1). `challan.view` is the same capability the
 * register itself is gated on, so this cannot link somewhere the tap would 404.
 * Every implemented role holds it today; the day the ACCOUNTANT row is switched
 * on, this stays honest by itself.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * IT IS THE SAME CARD GRAMMAR AS `SectionCards`, on purpose — same container
 * classes, same description/figure/footer-link rhythm — so it reads as one more
 * at-a-glance figure rather than as a notification bar bolted onto the screen.
 */
export function UnbilledDeliveries({
  summary,
}: {
  summary: DashboardSummaryResponse | undefined
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can } = useAuth()

  /*
   * NO SKELETON WHILE IT LOADS. The three cards above carry the loading state
   * for this row of the screen; a fourth skeleton that resolves to nothing on
   * most days would be a placeholder for an absence — worse than the absence.
   */
  const count = summary?.unbilledDeliveries?.count ?? 0
  if (count <= 0 || !can("challan.view")) return null

  return (
    <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs lg:px-6">
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("home.card.unbilledDeliveries")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {count}
          </CardTitle>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          {/*
            * `routes.challansUnbilled` is `/challans?openOnly=true` — the
            * register OPENS ALREADY FILTERED, on the same `openOnly` predicate
            * that produced this number (`unbilledChallanWhere()` on the API
            * side). A link to the unfiltered register would show 214 challans to
            * someone who was told there were 3, and the count would stop being
            * believed within a week.
            */}
          <Link
            to={routes.challansUnbilled}
            className="line-clamp-1 flex gap-2 font-medium hover:underline"
          >
            {t("home.card.openUnbilled")} <ArrowRightIcon className="size-4" />
          </Link>
          <div className="text-muted-foreground">{t("home.card.unbilledHelp")}</div>
        </CardFooter>
      </Card>
    </div>
  )
}
